/**
 * 七日签到奖励弹窗 — 业务逻辑
 */

import { cycleSignGetInfoApi, cycleSignSignApi } from '@/api/checkInReward'
import { createDefaultCheckInState, formatBonus, mapCheckInInfo } from '../../../js/mapper.js'

export function useCheckInReward(emit) {
  const { t } = useI18n()

  /** 签到状态（接口数据映射后） */
  const checkInState = reactive(createDefaultCheckInState())

  /** 拉取签到信息 loading */
  const isInfoLoading = ref(false)
  /** 签到 / 按钮操作 loading */
  const isActionLoading = ref(false)
  /**
   * 第 7 天霓虹 loading：
   * 签到第 7 天 / 锁定态去充值时开启，直到充值支付弹窗出现（或失败）才结束
   */
  const isNeonLoading = ref(false)

  /** 卡片背景图 URL */
  const cardBgUrl = getImageUrlWithCDN_LOCAL('/home2/atividade-popup-crad-bg.webp')
  /** 兔子装饰图 URL */
  const rabbitUrl = getImageUrlWithCDN_LOCAL('/home2/rabbit_model.webp')
  /** 金币奖励图标 URL */
  const coinUrl = getImageUrlWithCDN_LOCAL('/home2/gold_coin_rewards.png')

  /** 7 天奖励列表 */
  const dailyRewards = computed(() => checkInState.dailyRewards)
  /** 是否尚未签到（signCount === 0） */
  const isNotSigned = computed(() => checkInState.signCount === 0)
  /** 是否已签满 7 天（含锁定待充值） */
  const isCompleted = computed(() => {
    const totalDays = checkInState.dailyRewards.length || 7
    return checkInState.isLocked || checkInState.signCount >= totalDays
  })
  /** 储蓄罐累计金额（标题展示） */
  const accumulatedBonus = computed(() => checkInState.piggyAmount)
  /** 解锁所需充值金额 */
  const unlockRechargeAmount = computed(() => checkInState.unlockRechargeAmount)

  /** 是否即将签第 7 天（已满 6 天且今日可签） */
  const isAboutToSignDay7 = computed(() => {
    if (checkInState.isLocked || !checkInState.canSignToday) return false
    const totalDays = checkInState.dailyRewards.length
    // 固定 7 天周期：已签 6 天 → 本次即第 7 天
    if (checkInState.signCount === 6) return true
    // 兼容奖励列表长度动态的情况
    return totalDays > 0 && checkInState.signCount === totalDays - 1
  })

  /** Loading 文案拆成字符，供打字机动画使用 */
  const loadingChars = computed(() => t('home-ad-popup-index.common.loading').split(''))

  /** 判断某天是否已领取 */
  const isDayCollected = (day) => day <= checkInState.signCount

  /** 组装传给充值弹窗的数据 */
  const getRechargePayload = () => ({
    totalBonus: checkInState.piggyAmount || checkInState.totalReward, // 累计/总额
    depositAmount: checkInState.unlockRechargeAmount, // 解锁充值金额
  })

  /** 主按钮文案 */
  const actionButtonText = computed(() => {
    if (checkInState.isLocked) {
      return t('home-ad-popup-index.checkIn.depositAndClaimAll', {
        amount: formatBonus(checkInState.unlockRechargeAmount),
      })
    } else if (!checkInState.canSignToday) {
      return t('home-ad-popup-index.checkIn.checkedInToday')
    }
    return t('home-ad-popup-index.checkIn.checkInNow')
  })

  /** 主按钮是否禁用 */
  const isActionDisabled = computed(() => {
    if (isInfoLoading.value || isActionLoading.value || isNeonLoading.value) return true
    if (!checkInState.enabled) return true
    if (checkInState.isLocked) return false // 锁定态可点充值
    return !checkInState.canSignToday // 今日已签则禁用
  })

  /** 开启第 7 天霓虹 loading（与满 7 天锁定态同一套效果） */
  const startNeonLoading = () => {
    isNeonLoading.value = true
  }

  /** 结束第 7 天霓虹 loading（支付弹窗出现失败 / 外部取消时调用） */
  const stopNeonLoading = () => {
    isNeonLoading.value = false
    isActionLoading.value = false
  }

  /** 写入接口返回的签到状态 */
  const applyCheckInInfo = (raw) => {
    Object.assign(checkInState, mapCheckInInfo(raw))
  }

  /** 拉取签到信息 /cycleSign/getInfo */
  const fetchInfo = async () => {
    if (isInfoLoading.value) return null
    isInfoLoading.value = true
    try {
      const { code, data, msg } = await cycleSignGetInfoApi()
      if (code == 1) {
        applyCheckInInfo(data)
        return data
      }
      if (msg) showToast(msg)
      return null
    } catch (error) {
      console.error('[CheckInReward] fetchInfo error:', error)
      return null
    } finally {
      isInfoLoading.value = false
    }
  }

  /**
   * 执行签到 /cycleSign/sign
   * @param {{ keepLoading?: boolean }} options keepLoading 为 true 时不在 finally 清 loading（第 7 天霓虹态）
   */
  const doCheckIn = async (options = {}) => {
    const { keepLoading = false } = options
    if (isActionLoading.value) return false
    isActionLoading.value = true
    emit('request-state-change', true)
    let success = false
    try {
      const { code, data, msg } = await cycleSignSignApi()
      if (code == 1) {
        applyCheckInInfo(data)
        success = true
        return true
      }
      if (msg) showToast(msg)
      return false
    } catch (error) {
      console.error('[CheckInReward] doCheckIn error:', error)
      return false
    } finally {
      // 第 7 天仅在请求成功时保持 loading；请求超时或其他失败必须立即恢复按钮
      if (!keepLoading || !success) {
        isActionLoading.value = false
        isNeonLoading.value = false
      }
      emit('request-state-change', false)
    }
  }

  /** 主按钮点击：签到 / 打开充值 / 打开充值签收 */
  const handleActionClick = async () => {
    if (isActionDisabled.value) return

    // 锁定态（已满 7 天）：霓虹 loading → 直接发起充值支付
    if (checkInState.isLocked) {
      startNeonLoading()
      await nextTick()
      emit('open-deposit', getRechargePayload())
      return
    }

    // 已满 6 天、点击签第 7 天：与满 7 天同一套霓虹 Loading
    const isDay7Sign = isAboutToSignDay7.value
    if (isDay7Sign) {
      startNeonLoading()
      await nextTick()
    }

    const success = await doCheckIn({ keepLoading: isDay7Sign })
    if (!success) {
      stopNeonLoading()
      return
    }

    showToast(t('home-ad-popup-index.checkIn.successToast'))

    // 第 7 天签完进入锁定：保持霓虹 loading，打开充值签收弹窗
    if (checkInState.isLocked) {
      startNeonLoading()
      await nextTick()
      emit('open-recharge', getRechargePayload())
      return
    }

    // 普通签到：延迟关闭
    emit('sign-success')
  }

  return {
    checkInState,
    cardBgUrl,
    rabbitUrl,
    coinUrl,
    dailyRewards,
    isNotSigned,
    isCompleted,
    accumulatedBonus,
    unlockRechargeAmount,
    formatBonus,
    isDayCollected,
    actionButtonText,
    isActionDisabled,
    isActionLoading,
    isNeonLoading,
    loadingChars,
    handleActionClick,
    fetchInfo,
    applyCheckInInfo,
    stopNeonLoading,
  }
}
